import React, { useEffect, useState, useRef } from 'react';
import { useValueVisibility } from '../contexts/ValueVisibilityContext';
import { useML } from '../contexts/MLContext';
import { getAuthHeaders, WORKER_URL } from '../utils/api';
import OrdersModal from './Modal/OrdersModal';
import QuestionModal from './Modal/QuestionModal';
import SalesChart from './SalesChart';
import DashboardHeader from './DashboardHeader';
import './styles/animations.css';
import ShippingHeader from '../components/ShippingHeader';
import { Volume2, VolumeX } from 'lucide-react';

const handleExpiredToken = () => {
  localStorage.removeItem('ml_token');
  localStorage.removeItem('ml_refresh_token');
  localStorage.removeItem('ml_user_id');
  window.location.href = '/settings';
};

function DashboardMain({ menuOpen }) {
  const { showValues } = useValueVisibility();
  const { isMLConnected } = useML();

  const [salesData, setSalesData] = useState([]);
  const [todaySales, setTodaySales] = useState(0);
  const [todayOrders, setTodayOrders] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [storeName, setStoreName] = useState('');
  const [questions, setQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [isQuestionsModalOpen, setIsQuestionsModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);

  const [monthlyMetrics, setMonthlyMetrics] = useState({
    current: { sales: 0, orders: 0 },
    lastMonth: { sales: 0, orders: 0 },
    changes: { salesPercentage: 0, ordersPercentage: 0, unitsPercentage: 0 }
  });

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ordersByType, setOrdersByType] = useState({
    full: { orders: [], total: 0, count: 0 },
    flex: { orders: [], total: 0, count: 0 },
    agency: { orders: [], total: 0, count: 0 },
    withdrawal: { orders: [], total: 0, count: 0 }
  });

  const [shippingTypes, setShippingTypes] = useState({
    activeTypes: { xd_drop_off: false, drop_off: false, cross_docking: false, fulfillment: false },
    schedules: { agency: null, flex: null, withdrawal: null }
  });

  const [salesSoundEnabled, setSalesSoundEnabled] = useState(
    () => localStorage.getItem('sound_sales') !== 'false'
  );
  const [questionsSoundEnabled, setQuestionsSoundEnabled] = useState(
    () => localStorage.getItem('sound_questions') !== 'false'
  );
  const [showSoundModal, setShowSoundModal] = useState(
    () => sessionStorage.getItem('sound_unlocked') !== 'true'
  );
  const prevTodayOrders = useRef(null);
  const prevQuestionsCount = useRef(null);
  const questionsAlertInterval = useRef(null);
  const audioCtxRef = useRef(null);
  const audioBuffersRef = useRef({});

  const getAudioContext = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
    }
    return audioCtxRef.current;
  };

  const loadBuffer = async (file) => {
    if (audioBuffersRef.current[file]) return audioBuffersRef.current[file];
    const ctx = getAudioContext();
    const res = await fetch(`/sounds/${file}`);
    const arr = await res.arrayBuffer();
    const buf = await ctx.decodeAudioData(arr);
    audioBuffersRef.current[file] = buf;
    return buf;
  };

  const playSound = async (file) => {
    try {
      const ctx = getAudioContext();
      if (ctx.state === 'suspended') await ctx.resume();
      const buffer = await loadBuffer(file);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
    } catch (e) {}
  };

  const unlockAudio = async () => {
    const ctx = getAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();
  };

  const startQuestionsAlert = () => {
    if (questionsAlertInterval.current) return;
    questionsAlertInterval.current = setInterval(() => {
      playSound('pergunta.mp3');
    }, 60000);
  };

  const stopQuestionsAlert = () => {
    if (questionsAlertInterval.current) {
      clearInterval(questionsAlertInterval.current);
      questionsAlertInterval.current = null;
    }
  };

  // =================== FUNÇÕES DE DADOS ===================

  const processOrdersData = (orders = []) => {
    let todayTotal = 0;
    let totalOrders = 0;
    const hourlyData = Array.from({ length: 24 }, (_, hour) => ({
      hora: `${hour.toString().padStart(2, '0')}:00`,
      hoje: 0,
      ontem: 0
    }));

    if (!orders.length) return { hourlyData, todayTotal, totalOrders };

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    orders.forEach(order => {
      try {
        const orderDate = new Date(order.date_created);
        const orderDay = new Date(orderDate);
        orderDay.setHours(0, 0, 0, 0);
        if (orderDay.getTime() === today.getTime()) {
          const amount = parseFloat(order.total_amount) || 0;
          hourlyData[orderDate.getHours()].hoje += amount;
          todayTotal += amount;
          totalOrders++;
        }
      } catch (error) {
        console.error('Erro ao processar ordem:', error);
      }
    });

    return { hourlyData, todayTotal, totalOrders };
  };

  const fetchSales = async () => {
    try {
      setIsLoading(true);
      const response = await fetch(`${WORKER_URL}/api/sales`, {
        headers: getAuthHeaders()
      });
      if (response.status === 401) { handleExpiredToken(); return; }
      const data = await response.json();
      const { hourlyData, todayTotal, totalOrders } = processOrdersData(data?.results || []);
      setSalesData(hourlyData);
      setTodaySales(todayTotal);
      setTodayOrders(totalOrders);
    } catch (error) {
      console.error('Erro ao buscar vendas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const response = await fetch(`${WORKER_URL}/api/questions`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setQuestions(data?.questions || []);
    } catch (error) {
      console.error('Erro ao buscar perguntas:', error);
    }
  };

  const fetchMonthlyMetrics = async () => {
    try {
      const response = await fetch(`${WORKER_URL}/api/metrics/monthly`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Falha ao buscar métricas mensais');
      setMonthlyMetrics(await response.json());
    } catch (error) {
      console.error('Erro ao buscar métricas mensais:', error);
    }
  };

  const fetchOrdersByType = async () => {
    setOrdersLoading(true);
    try {
      const response = await fetch(`${WORKER_URL}/api/orders/by-type`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) throw new Error('Falha ao buscar pedidos');
      const data = await response.json();
      setOrdersByType({
        full: data.full || { orders: [], total: 0, count: 0 },
        flex: data.flex || { orders: [], total: 0, count: 0 },
        agency: data.agency || { orders: [], total: 0, count: 0 },
        withdrawal: data.withdrawal || { orders: [], total: 0, count: 0 }
      });
    } catch (error) {
      console.error('Erro ao buscar pedidos:', error);
      setOrdersByType({
        full: { orders: [], total: 0, count: 0 },
        flex: { orders: [], total: 0, count: 0 },
        agency: { orders: [], total: 0, count: 0 },
        withdrawal: { orders: [], total: 0, count: 0 }
      });
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchStoreData = async () => {
    try {
      const response = await fetch(`${WORKER_URL}/api/user/me`, {
        headers: getAuthHeaders()
      });
      const data = await response.json();
      setStoreName(data.nickname);
    } catch (error) {
      console.error('Erro ao buscar dados da loja:', error);
    }
  };

  const fetchCutoffTimes = async () => {
    try {
      const response = await fetch(`${WORKER_URL}/api/shipping/methods`, {
        headers: getAuthHeaders()
      });
      if (!response.ok) return;
      setShippingTypes(await response.json());
    } catch (error) {
      console.error('Erro ao buscar horários de corte:', error);
    }
  };

  // =================== HANDLERS ===================

  const handleAnswerQuestion = async (questionId, answerText) => {
    try {
      await fetch(`${WORKER_URL}/api/questions/${questionId}/answer`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: answerText })
      });
      setQuestions(prev => prev.filter(q => q.id !== questionId));
      if (questions.length === 1) setIsQuestionsModalOpen(false);
    } catch (error) {
      console.error('Erro ao responder pergunta:', error);
    }
  };

  const handleAnswer = async () => {
    if (selectedQuestion && answer) {
      await handleAnswerQuestion(selectedQuestion.id, answer);
      setAnswer('');
      setSelectedQuestion(null);
    }
  };

  useEffect(() => {
    if (isLoading) return;
    if (prevTodayOrders.current === null) {
      prevTodayOrders.current = todayOrders;
      return;
    }
    if (todayOrders > prevTodayOrders.current && salesSoundEnabled) {
      playSound('venda.mp3');
    }
    prevTodayOrders.current = todayOrders;
  }, [todayOrders, isLoading, salesSoundEnabled]);

  useEffect(() => {
    if (!questionsSoundEnabled) {
      stopQuestionsAlert();
      return;
    }
    if (prevQuestionsCount.current === null) {
      prevQuestionsCount.current = questions.length;
      if (questions.length > 0) startQuestionsAlert();
      return;
    }
    if (questions.length > 0) {
      if (questions.length > prevQuestionsCount.current) {
        playSound('pergunta.mp3');
      }
      startQuestionsAlert();
    } else {
      stopQuestionsAlert();
    }
    prevQuestionsCount.current = questions.length;
  }, [questions.length, questionsSoundEnabled]);

  // =================== EFFECTS ===================

  useEffect(() => {
    fetchStoreData();
    fetchSales();
    fetchQuestions();
    fetchOrdersByType();
    fetchMonthlyMetrics();
    fetchCutoffTimes();

    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    const poller = setInterval(() => {
      fetchSales();
      fetchQuestions();
      fetchMonthlyMetrics();
    }, 30000);

    return () => {
      clearInterval(timer);
      clearInterval(poller);
      stopQuestionsAlert();
    };
  }, []);

  // =================== RENDER ===================

  const handleSoundModalChoice = async (activate) => {
    sessionStorage.setItem('sound_unlocked', 'true');
    setShowSoundModal(false);
    if (activate) {
      await unlockAudio();
    } else {
      setSalesSoundEnabled(false);
      setQuestionsSoundEnabled(false);
      localStorage.setItem('sound_sales', 'false');
      localStorage.setItem('sound_questions', 'false');
    }
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      overflow: 'hidden',
      position: 'absolute',
      top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: '#FFFFFF'
    }}>
      <DashboardHeader
        currentTime={currentTime}
        isMLConnected={isMLConnected}
        storeName={storeName}
      />

      <ShippingHeader shippingTypes={shippingTypes} />

      {showSoundModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ backgroundColor: 'white', borderRadius: '10px', padding: '24px 28px', boxShadow: '0 4px 20px rgba(0,0,0,0.2)', textAlign: 'center', minWidth: '260px' }}>
            <p style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: 600, color: '#2D3277' }}>🔔 Ativar sons?</p>
            <p style={{ margin: '0 0 20px 0', fontSize: '0.8rem', color: '#666' }}>Notificações de pedidos e perguntas</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button onClick={() => handleSoundModalChoice(true)} style={{ backgroundColor: '#2D3277', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 24px', cursor: 'pointer', fontWeight: 600 }}>Sim</button>
              <button onClick={() => handleSoundModalChoice(false)} style={{ backgroundColor: '#eee', color: '#333', border: 'none', borderRadius: '6px', padding: '8px 24px', cursor: 'pointer' }}>Não</button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        background: 'linear-gradient(180deg, #FFE600 0%, #FFFFFF 70%)',
        padding: '10px 16px 0',
        flex: 1,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}>

        {/* Cards Superiores */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, 1fr)',
          gap: '8px',
          marginBottom: '8px'
        }}>

          {/* Vendas Hoje */}
          <div style={{ backgroundColor: 'white', padding: '8px 10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: '#2D3277', margin: '0 0 2px 0', fontSize: '0.75rem' }}>Vendas Hoje</h3>
            <p style={{ fontSize: '1rem', margin: '0', color: '#00a650' }}>
              {showValues
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(todaySales)
                : 'R$ ****'}
            </p>
          </div>

          {/* Pedidos */}
          <div style={{ backgroundColor: 'white', padding: '8px 10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
              <h3 style={{ color: '#2D3277', margin: 0, fontSize: '0.75rem' }}>Pedidos</h3>
              <button
                onClick={() => { setSalesSoundEnabled(p => { const next = !p; localStorage.setItem('sound_sales', String(next)); if (next) { unlockAudio(); playSound('venda.mp3'); } return next; }); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: salesSoundEnabled ? '#2D3277' : '#ccc', display: 'flex' }}
                title={salesSoundEnabled ? 'Desligar som' : 'Ligar som'}
              >
                {salesSoundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
            </div>
            <p style={{ fontSize: '1rem', margin: '0', color: '#ff8000' }}>{todayOrders}</p>
          </div>

          {/* Perguntas Novas */}
          <div
            style={{
              padding: '8px 10px',
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              cursor: 'pointer',
              animation: questions.length > 0 ? 'colorPulse 5s ease-in-out infinite' : 'none',
              backgroundColor: questions.length > 0 ? '#2D3277' : 'white'
            }}
            onClick={() => questions.length > 0 && setIsQuestionsModalOpen(true)}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
              <h3 style={{ color: questions.length > 0 ? 'white' : '#2D3277', margin: 0, fontSize: '0.75rem' }}>
                Perguntas Novas
              </h3>
              <button
                onClick={(e) => { e.stopPropagation(); setQuestionsSoundEnabled(p => { const next = !p; localStorage.setItem('sound_questions', String(next)); if (next) { unlockAudio(); playSound('pergunta.mp3'); } return next; }); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: questions.length > 0 ? 'white' : (questionsSoundEnabled ? '#2D3277' : '#ccc'), display: 'flex' }}
                title={questionsSoundEnabled ? 'Desligar som' : 'Ligar som'}
              >
                {questionsSoundEnabled ? <Volume2 size={14} /> : <VolumeX size={14} />}
              </button>
            </div>
            <p style={{ fontSize: '1rem', margin: '0', color: questions.length > 0 ? 'white' : '#ff0000' }}>
              {questions.length}
            </p>
          </div>

          {/* Vendas Brutas */}
          <div style={{ backgroundColor: 'white', padding: '8px 10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: '#2D3277', margin: '0 0 2px 0', fontSize: '0.75rem' }}>Vendas Brutas</h3>
            <div style={{ fontSize: '1rem', margin: '0', color: '#00a650' }}>
              {showValues
                ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(monthlyMetrics.current.sales)
                : 'R$ ****'}
            </div>
            <p style={{ fontSize: '0.7rem', margin: '2px 0 0 0', color: monthlyMetrics.changes.salesPercentage >= 0 ? '#00a650' : '#ff0000' }}>
              {monthlyMetrics.changes.salesPercentage >= 0 ? '+' : ''}{monthlyMetrics.changes.salesPercentage}% vs mesmo período
            </p>
          </div>

          {/* Pedidos do Mês */}
          <div style={{ backgroundColor: 'white', padding: '8px 10px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
            <h3 style={{ color: '#2D3277', margin: '0 0 2px 0', fontSize: '0.75rem' }}>Pedidos do Mês</h3>
            <div style={{ fontSize: '1rem', margin: '0', color: '#2D3277' }}>
              {monthlyMetrics.current.units || 0}
            </div>
            <p style={{ fontSize: '0.7rem', margin: '2px 0 0 0', color: monthlyMetrics.changes.unitsPercentage >= 0 ? '#00a650' : '#ff0000' }}>
              {monthlyMetrics.changes.unitsPercentage >= 0 ? '+' : ''}{monthlyMetrics.changes.unitsPercentage}% vs mesmo período
            </p>
          </div>
        </div>

        {/* Pedidos Pendentes */}
        <div style={{ marginBottom: '6px' }}>
          <h3 style={{ color: '#2D3277', margin: '0 0 4px 0', fontSize: '0.9rem', textAlign: 'center', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
            Pedidos Pendentes
            {ordersLoading && (
              <span style={{ display: 'inline-block', width: '10px', height: '10px', border: '2px solid #2D3277', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
            )}
          </h3>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', gap: '12px', minWidth: 0 }}>

              {shippingTypes.activeTypes?.full && (
                <div style={{ backgroundColor: 'white', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderTop: '3px solid #00a650', cursor: 'pointer', minWidth: '160px' }}
                  onClick={() => { setSelectedType('full'); setIsModalOpen(true); }}>
                  <h3 style={{ color: '#2D3277', margin: '0 0 4px 0', fontSize: '0.75rem' }}>Pendentes FULL</h3>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ margin: '2px 0' }}>Pedidos: <strong>{ordersByType.full?.count}</strong></div>
                    <div style={{ margin: '2px 0' }}>Total: <strong>{showValues ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ordersByType?.full?.total || 0) : 'R$ ****'}</strong></div>
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '2px' }}>Em preparação para envio</div>
                  </div>
                </div>
              )}

              {shippingTypes.activeTypes?.flex && (
                <div style={{ backgroundColor: 'white', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderTop: '3px solid #2D3277', cursor: 'pointer', minWidth: '160px' }}
                  onClick={() => { setSelectedType('flex'); setIsModalOpen(true); }}>
                  <h3 style={{ color: '#2D3277', margin: '0 0 4px 0', fontSize: '0.75rem' }}>Pendentes FLEX</h3>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ margin: '2px 0' }}>Pedidos: <strong>{ordersByType?.flex?.count}</strong></div>
                    <div style={{ margin: '2px 0' }}>Total: <strong>{showValues ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ordersByType?.flex?.total) : 'R$ ****'}</strong></div>
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '2px' }}>Aguardando envio</div>
                  </div>
                </div>
              )}

              {shippingTypes.activeTypes?.agency && (
                <div style={{ backgroundColor: 'white', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderTop: '3px solid #FFE600', cursor: 'pointer', minWidth: '160px' }}
                  onClick={() => { setSelectedType('agency'); setIsModalOpen(true); }}>
                  <h3 style={{ color: '#2D3277', margin: '0 0 4px 0', fontSize: '0.75rem' }}>Pendentes AGÊNCIA</h3>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ margin: '2px 0' }}>Pedidos: <strong>{ordersByType?.agency?.count}</strong></div>
                    <div style={{ margin: '2px 0' }}>Total: <strong>{showValues ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ordersByType?.agency?.total) : 'R$ ****'}</strong></div>
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '2px' }}>Aguardando nota fiscal/etiqueta</div>
                  </div>
                </div>
              )}

              {shippingTypes.activeTypes?.withdrawal && (
                <div style={{ backgroundColor: 'white', padding: '8px 12px', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderTop: '3px solid #ff4444', cursor: 'pointer', minWidth: '160px' }}
                  onClick={() => { setSelectedType('withdrawal'); setIsModalOpen(true); }}>
                  <h3 style={{ color: '#2D3277', margin: '0 0 4px 0', fontSize: '0.75rem' }}>Pendentes RETIRADA</h3>
                  <div style={{ fontSize: '0.8rem' }}>
                    <div style={{ margin: '2px 0' }}>Pedidos: <strong>{ordersByType?.withdrawal?.count || 0}</strong></div>
                    <div style={{ margin: '2px 0' }}>Total: <strong>{showValues ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(ordersByType?.withdrawal?.total || 0) : 'R$ ****'}</strong></div>
                    <div style={{ fontSize: '0.7rem', color: '#666', marginTop: '2px' }}>Aguardando retirada</div>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <SalesChart
            menuOpen={menuOpen}
            data={salesData}
            showValues={showValues}
          />
        </div>
      </div>

      <QuestionModal
        isOpen={isQuestionsModalOpen}
        onClose={() => setIsQuestionsModalOpen(false)}
        questions={questions}
        onAnswer={handleAnswerQuestion}
      />

      <OrdersModal
        isOpen={isModalOpen}
        onClose={() => { setIsModalOpen(false); setSelectedType(null); }}
        orders={selectedType ? ordersByType[selectedType].orders : []}
        type={selectedType}
      />
    </div>
  );
}

export default DashboardMain;
